import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Shelter } from "@/types/shelter";

export const useVerifyOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ shelterId, verified }: { shelterId: string; verified: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nie jesteś zalogowany");

      const updateData = verified
        ? {
            organization_verified: true,
            organization_verified_at: new Date().toISOString(),
            // Clear pending flag when verifying
            organization_verification_pending: false,
            contacted: true,
          }
        : {
            organization_verified: false,
            organization_verified_at: null,
          };

      const { error } = await supabase
        .from("shelters")
        .update(updateData)
        .eq("id", shelterId);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelter-updates"] });
      queryClient.invalidateQueries({ queryKey: ["shelters-for-verification"] });
    },
  });
};

// Hook to dismiss/reject verification request (without verifying)
export const useDismissVerificationRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shelterId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nie jesteś zalogowany");

      const { error } = await supabase
        .from("shelters")
        .update({
          organization_verification_pending: false,
          organization_verification_requested_at: null,
        })
        .eq("id", shelterId);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelters-for-verification"] });
    },
  });
};

// Hook to toggle contacted status
export const useToggleContacted = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ shelterId, contacted }: { shelterId: string; contacted: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nie jesteś zalogowany");

      const { error } = await supabase
        .from("shelters")
        .update({ contacted })
        .eq("id", shelterId);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelters-for-verification"] });
    },
  });
};

// Hook to completely delete a shelter from the database
export const useDeleteShelter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shelterId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nie jesteś zalogowany");

      // First delete any related shelter_updates
      const { error: updatesError } = await supabase
        .from("shelter_updates")
        .delete()
        .eq("shelter_id", shelterId);

      if (updatesError) {
        console.error("Error deleting shelter updates:", updatesError);
        // Continue anyway - shelter might not have any updates
      }

      // Delete any related graphic_downloads
      const { error: downloadsError } = await supabase
        .from("graphic_downloads")
        .delete()
        .eq("shelter_id", shelterId);

      if (downloadsError) {
        console.error("Error deleting graphic downloads:", downloadsError);
        // Continue anyway
      }

      // Now delete the shelter
      const { error } = await supabase
        .from("shelters")
        .delete()
        .eq("id", shelterId);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelter-updates"] });
      queryClient.invalidateQueries({ queryKey: ["shelters-for-verification"] });
    },
  });
};

export const useVerifiedOrganizations = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["verified-organizations"],
    queryFn: async (): Promise<Shelter[]> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return [];

      const { data, error } = await supabase
        .from("shelters")
        .select("*")
        .eq("organization_verified", true)
        .order("organization_verified_at", { ascending: false });

      if (error) {
        console.error("Error fetching verified organizations:", error);
        throw new Error(error.message);
      }

      return (data || []).map((s) => ({
        id: s.id,
        name: s.name,
        city: s.city,
        region: s.region,
        address: s.address,
        postal_code: s.postal_code ?? null,
        lat: s.lat,
        lng: s.lng,
        phone: s.phone,
        email: s.email,
        website: s.website,
        current_needs: s.current_needs,
        urgent_needs: s.urgent_needs,
        urgent_needs_set_at: s.urgent_needs_set_at,
        dog_walking_available: s.dog_walking_available,
        volunteering_available: s.volunteering_available,
        dogs_available: s.dogs_available,
        cats_available: s.cats_available,
        volunteer_info: s.volunteer_info,
        description: s.description,
        last_updated: s.last_updated ?? s.created_at,
        created_at: s.created_at,
        data_verified: s.data_verified ?? false,
        organization_verified: s.organization_verified ?? false,
        organization_verified_at: s.organization_verified_at ?? null,
        organization_verification_pending: (s as any).organization_verification_pending ?? false,
        organization_verification_requested_at: (s as any).organization_verification_requested_at ?? null,
        contacted: (s as any).contacted ?? false,
      }));
    },
    enabled,
  });
};

export const useAllSheltersForVerification = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["shelters-for-verification"],
    queryFn: async (): Promise<Shelter[]> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return [];

      const { data, error } = await supabase
        .from("shelters")
        .select("*")
        .order("name", { ascending: true });

      if (error) {
        console.error("Error fetching shelters:", error);
        throw new Error(error.message);
      }

      return (data || []).map((s) => ({
        id: s.id,
        name: s.name,
        city: s.city,
        region: s.region,
        address: s.address,
        postal_code: s.postal_code ?? null,
        lat: s.lat,
        lng: s.lng,
        phone: s.phone,
        email: s.email,
        website: s.website,
        current_needs: s.current_needs,
        urgent_needs: s.urgent_needs,
        urgent_needs_set_at: s.urgent_needs_set_at,
        dog_walking_available: s.dog_walking_available,
        volunteering_available: s.volunteering_available,
        dogs_available: s.dogs_available,
        cats_available: s.cats_available,
        volunteer_info: s.volunteer_info,
        description: s.description,
        last_updated: s.last_updated ?? s.created_at,
        created_at: s.created_at,
        data_verified: s.data_verified ?? false,
        organization_verified: s.organization_verified ?? false,
        organization_verified_at: s.organization_verified_at ?? null,
        organization_verification_pending: (s as any).organization_verification_pending ?? false,
        organization_verification_requested_at: (s as any).organization_verification_requested_at ?? null,
        contacted: (s as any).contacted ?? false,
      }));
    },
    enabled,
  });
};
